// The one durable edge: a bookmaker whose price is longer than the market says
// the side is worth.
//
// Everything the app already computes judges a price against a fair one that
// INCLUDES the book being judged — `board-view.js`'s cell edge averages every
// book that prices the match, the outlier among them dragging the average
// toward itself. That is the right figure for a board cell (it is a fact about
// the whole board, and it never flatters a single shop), and it is the wrong
// one for a detector: the more out of line a book is, the more it dilutes the
// measure of how out of line it is.
//
// So this module does what `runStrategy` does when it shops the board — it
// takes the market's opinion WITHOUT the book it is about to bet at
// (`consensus(..., { exclude })`) — and asks whether the price clears the fair
// one by enough to be worth printing.
//
// PURE. A universe (or a bare match) goes in and plain objects come out: no
// store, no clock of its own, no fetch, no names it does not already hold. The
// short bookmaker names a screen prints are applied by `board-view.js`, which
// is the layer that knows what a punter calls a shop.

import {
  bookFair, consensus, overroundBandFor, selectionsOf,
} from './universe.js';
// The exchange read as an OPINION, net of commission — the same reading the
// Racing tab's fair column and the thin-board fallback already use, so the
// reference price here and the one on the screen cannot differ.
import { bookFairNet } from './models/sharpAnchor.js';
import { isRacingSport } from './teams.js';
// The commission correction now lives BELOW `consensus` and is applied inside
// it (engine review #14), so nothing here deflates a board before asking it a
// question about probabilities. `deflated` survives for `arbsFor` alone, which
// splits real dollars across real prices.
import { COMMISSION, EXCHANGES, deflated, isExchange, netPrice } from './exchange.js';
// WHEN THE EXCHANGE IS WORTH BELIEVING ABOUT A RACE, which is not the same
// question as whether it has a price up. A leaf module (it imports nothing), so
// the engine reading it costs the same as a constant.
import { codeFor, exchangeTrust, venueOfKey } from '../racing/trust.js';

/**
 * How far over the market's fair price a book has to be before the app says
 * anything. 3% of the price, not of the probability — it is the figure a
 * punter can check with a calculator against the two numbers on the screen.
 */
export const MIN_EDGE = 0.03;

/**
 * How many books must be pricing the WHOLE match — the consensus voters,
 * counted WITH the flagged book among them.
 *
 * Counting them with the flagged book included, rather than requiring three
 * others, is the deliberate choice: it means the fair price the edge is
 * measured against always rests on at least two independent opinions, which is
 * the fewest that can disagree with each other, while a match nobody but the
 * outlier and one other shop has priced is left alone. A book that quotes only
 * one side is not a voter at all — it cannot be devigged — so on such a match
 * the three are three genuine full-match markets.
 */
export const MIN_BOOKS = 3;

/**
 * THE SAME TWO QUESTIONS, ASKED HARDER OF A RACE.
 *
 * A head-to-head is two prices a punter can hold in their head, and 3% over a
 * fair price built on three books is a finding. A twelve-runner field is
 * twelve prices, each one carrying a twelfth of a much wider round, and the
 * books disagree about the tail of a race the way they never disagree about a
 * two-way market — 3% over three books would flag a handful of runners in
 * every race on the card, which is the same as flagging none of them. Five per
 * cent, over four books pricing the whole active field, is the figure that
 * leaves a race quiet unless something is actually out of line.
 */
export const RACING_MIN_EDGE = 0.05;
export const RACING_MIN_BOOKS = 4;

/**
 * WHICH DEVIG THE BOARD IS READ WITH, and why it is still power on a race.
 *
 * The racing STRATEGIES devig with Shin now (engine review #7): Shin models the
 * favourite–longshot bias of a big field, which is the part of a fourteen-runner
 * vector power spreads evenly and gets wrong. The BOARD — the Matches
 * highlight, the Racing tab's fair column and everything `outliersFor` prints —
 * stays on power, deliberately: it is what the screens have always shown, the
 * two methods disagree most on exactly the tail runners the racing floors exist
 * to keep quiet, and telling a punter a runner is 4% over on Tuesday and 6% over
 * on Wednesday because the app changed its arithmetic is not a finding.
 *
 * Engine review #3 has since landed, and it landed in `runStrategy` rather than
 * here: a LEG has to clear its floors under all three devigs before it prints
 * (`CERTAINTY_METHODS`, devig.js). The board is not a leg — it is a price table
 * a punter reads, every side of every match, and a fair column that changed its
 * arithmetic between two Tuesdays would be the thing that note is about. So the
 * board still reads one method, and the method is power.
 */
export const DEVIG = 'power';

/** What a sport has to clear before a price is worth printing. */
export function thresholdsFor(sport) {
  return isRacingSport(sport)
    ? { minEdge: RACING_MIN_EDGE, minBooks: RACING_MIN_BOOKS }
    : { minEdge: MIN_EDGE, minBooks: MIN_BOOKS };
}

/**
 * The exchange's own figures, still reachable by the name every caller in the
 * app already uses. They live in `engine/exchange.js` now — a leaf, so that
 * `universe.js` can deflate inside `consensus` without a cycle.
 */
export const EXCHANGE_COMMISSION = COMMISSION;
export { EXCHANGES, isExchange, netPrice };

/**
 * The sides this match carries a real price for, in the engine's own order —
 * home/away/draw on a fixture, the ACTIVE field on a race, so a scratching the
 * books are still quoting is never one of them.
 */
const pricedSides = (match) =>
  selectionsOf(match).filter((s) => (match?.prices?.[s]?.length ?? 0) > 0);

/** What a side is called: a runner, a team, or the draw, which is neither. */
const sideName = (match, selection) =>
  match?.runners?.[selection]?.name ?? match?.[selection]?.name ?? 'Draw';

const usable = (row) => Number.isFinite(row?.price) && row.price > 1;

/** The longest price among rows, first in wins a tie — `bestPrice`'s own rule. */
function longest(rows) {
  let best = null;
  for (const row of rows) if (!best || row.price > best.price) best = row;
  return best;
}

/**
 * The market's fair probability for a side with one book's vote taken out, and
 * how many books are left voting. Null when the rest of the board has no
 * opinion at all.
 */
export function fairExcluding(match, selection, bookmaker, { devig = DEVIG, commission = EXCHANGE_COMMISSION } = {}) {
  return consensus(match, selection, { devig, exclude: bookmaker, commission });
}

// --- the longest of thirteen is not the market being wrong ---------------------
//
// THE ORDER-STATISTIC PROBLEM (engine review #8). This module measures the
// LONGEST price on the board against the mean of the rest, and calls the gap an
// edge. But the longest of n quotes is a maximum, and the maximum of n noisy
// unbiased quotes sits above their mean by construction — nobody has to be
// wrong about anything. If each book's implied probability for a side is the
// true probability plus noise of size sigma, then the best price's implied
// probability sits about E[max of n standard normals] x sigma BELOW the mean,
// and this module reads that entire gap as value. Worse, it grows with n: a
// board of thirteen books produces bigger false edges than a board of four, so
// the detector is loudest exactly where the market is best covered.
//
// TWO ANSWERS, and which one applies is a fact about the board:
//
//   1. THE EXCHANGE, WHERE THERE IS ONE AND IT IS WORTH BELIEVING. A Betfair
//      price is not one more opinion to average, it is the price the money
//      settled on, net of commission. Compared against it there is no maximum
//      being taken at all — one reference, one price — so the correction below
//      is not needed and is not applied. This is the same reading
//      `board-view.js` falls back to on a thin match and the Racing tab prints
//      in its exchange column.
//
//      ON A RACE THAT COMES WITH A CONDITION. Most AU racing markets fill in
//      the last four minutes, so at noon a WIN market can be two people and a
//      few hundred dollars, and "the price the money settled on" is a
//      description of nothing. `exchangeTrustFor` is the gate — traded volume
//      against a floor per code, or the clock alone where a delayed app key
//      cannot see volume — and a race that fails it is measured against the
//      local books like any race the exchange is not on.
//   2. THE CORRECTION, WHERE THERE IS NOT. Take off what the maximum of n
//      unbiased quotes would have produced on its own:
//
//          c(n) = k x sigma x E[max of n standard normals]
//
//      in probability units, off the fair probability the edge is measured
//      against. `edge = price x (pFair - c(n)) - 1` is the same arithmetic as
//      subtracting `price x c(n)` from the edge, and it is written this way
//      round so a screen can print a fair price and a percentage that check
//      against each other.
//
// SIGMA IS MEASURED OVER THE OTHER BOOKS, NOT INCLUDING THE LONGEST ONE, and
// that is the part it is easy to get wrong. The null this corrects for is "n
// unbiased quotes, one of which happened to come out highest", and sigma is
// that null's scale. Estimate it from a sample that still contains a genuine
// outlier and the outlier inflates its own correction — the bigger the finding,
// the more comes off it, which is a detector that cannot find anything. So the
// scale is the dispersion of exactly the books the fair price rests on: the
// same n - 1 shops, the flagged one left out of both. A sample with its extreme
// removed reads a little tight, which errs toward flagging; `k` errs the other
// way.
//
// `k` is a shrink, and it is there because the derivation assumes something a
// real board is not: n INDEPENDENT draws. Australian books copy each other, and
// the maximum of n correlated quotes sits closer to the mean than the maximum
// of n independent ones, so the full expected maximum would take off more than
// the artefact is worth and the detector would go quiet. Half of it is the
// honest middle — and the operator dedupe in `consensus` (engine review #5) is
// the other half of the same correlation problem, fixed where it belongs.

/**
 * E[max of n standard normals], n = 3..16. Tabulated rather than computed: the
 * closed forms stop at n = 5 and the asymptotic expansion is still 9% low at
 * n = 16, which is the whole range a real board occupies.
 */
export const EXPECTED_MAX = {
  3: 0.8463,
  4: 1.0294,
  5: 1.1630,
  6: 1.2672,
  7: 1.3522,
  8: 1.4236,
  9: 1.4850,
  10: 1.5388,
  11: 1.5864,
  12: 1.6292,
  13: 1.6680,
  14: 1.7034,
  15: 1.7359,
  16: 1.7660,
};

/** How much of the expected maximum is treated as artefact rather than disagreement. */
export const BEST_OF_N_K = 0.5;

/**
 * The table's figure for n quotes. Zero below three — two prices have a longer
 * and a shorter one and no distribution to speak of — and flat above sixteen,
 * which no board this app can build ever reaches (`BOOKMAKERS` is thirteen keys
 * and one of them is the exchange). Flat under-corrects rather than inventing
 * precision past the table.
 */
export function expectedMax(n) {
  if (!Number.isFinite(n) || n < 3) return 0;
  return EXPECTED_MAX[Math.min(Math.floor(n), 16)] ?? EXPECTED_MAX[16];
}

/** Population standard deviation of a list of numbers; 0 for fewer than two. */
export function dispersion(values) {
  const xs = (values ?? []).filter((v) => Number.isFinite(v));
  if (xs.length < 2) return 0;
  const mean = xs.reduce((sum, x) => sum + x, 0) / xs.length;
  const variance = xs.reduce((sum, x) => sum + (x - mean) ** 2, 0) / xs.length;
  return Math.sqrt(variance);
}

/**
 * How much of a best-of-n gap the maximum would have produced on its own, in
 * PROBABILITY units — `n` the number of quotes the longest was picked out of,
 * `sigma` the dispersion of the OTHER n - 1 (see the block above).
 */
export function bestOfNCorrection(n, sigma) {
  if (!Number.isFinite(sigma) || sigma <= 0) return 0;
  return BEST_OF_N_K * sigma * expectedMax(n);
}

/**
 * The exchange's own fair probability for a side, net of commission, and which
 * exchange said so — or null where none of them prices the whole match.
 */
function exchangeReference(match, selection, devig, commission) {
  for (const row of match?.prices?.[selection] ?? []) {
    if (!isExchange(row.bookmaker)) continue;
    const p = bookFairNet(match, selection, row.bookmaker, devig, commission);
    if (Number.isFinite(p) && p > 0) return { pFair: p, bookmaker: row.bookmaker };
  }
  return null;
}

/**
 * The traded total on this match's exchange rows, or null.
 *
 * One figure for the whole market, carried on every exchange row's `liquidity`
 * (lib/racing/exchange.js), so the first row that has one answers for the race.
 * Null on a delayed Betfair key, which is the ordinary case.
 */
function tradedOn(match) {
  for (const side of Object.keys(match?.prices ?? {})) {
    for (const row of match.prices[side] ?? []) {
      if (!isExchange(row.bookmaker)) continue;
      const matched = Number(row.liquidity);
      if (Number.isFinite(matched) && matched > 0) return matched;
    }
  }
  return null;
}

/** How many minutes until this race jumps, or null with no clock to read it by. */
function minutesToJump(match, now) {
  const at = Number(now);
  const start = new Date(match?.kickoff).getTime();
  if (!Number.isFinite(at) || !Number.isFinite(start)) return null;
  return (start - at) / 60000;
}

/**
 * IS THE EXCHANGE THE REFERENCE FOR THIS RACE, right now?
 *
 * A fixture's exchange price is a two-way market that has been traded all week;
 * a race's is a market that fills in the last four minutes, and reading a
 * lightly traded WIN market as "the price the money settled on" is how a runner
 * gets measured against nothing at all. So racing goes through the gate and
 * everything else does not.
 *
 * The whole answer is returned rather than a boolean: the screens print which
 * yardstick they used and why, and a second derivation of "why" would be a
 * second answer. See lib/racing/trust.js for the floors.
 */
export function exchangeTrustFor(match, { now = null } = {}) {
  if (!isRacingSport(match?.sport)) return { trusted: true, reason: null, basis: 'market', floor: null, label: 'exchange' };
  const totalMatched = tradedOn(match);
  const minutes = minutesToJump(match, now);
  return {
    ...exchangeTrust({
      totalMatched,
      minutesToJump: minutes,
      code: codeFor(match?.sport, match?.venueId ?? venueOfKey(match?.matchKey)),
    }),
    totalMatched,
    minutesToJump: minutes,
  };
}

/** Longest edge first, and a tie broken on the side so the order is stable. */
const byEdge = (a, b) => b.edge - a.edge || String(a.side).localeCompare(String(b.side));

/**
 * Every side of one match where the best bookmaker price is over the rest of
 * the market's fair price by `minEdge` or more.
 *
 * AN EXCHANGE IS NEVER THE FLAGGED BOOK. A punter cannot take an exchange's
 * back price the way they take a bookmaker's: it is somebody else's offer, for
 * whatever is actually there, and it moves as it is taken. So the exchange
 * votes on the fair price (net of commission, inside `consensus`) but is never
 * the thing printed as
 * "over the market". It appears only inside `arbsFor`, marked `exchange`.
 */
export function outliersFor(match, {
  minEdge,
  minBooks,
  devig = DEVIG,
  commission = EXCHANGE_COMMISSION,
  now = null,
} = {}) {
  // Per MATCH, not per board: one pass over a mixed card holds a race to the
  // racing figures and the football to the h2h ones. An explicit option still
  // wins — a caller that names a threshold means it.
  const floor = thresholdsFor(match?.sport);
  const edgeFloor = Number.isFinite(minEdge) ? minEdge : floor.minEdge;
  const booksFloor = Number.isFinite(minBooks) ? minBooks : floor.minBooks;
  // The board itself, undeflated: `consensus` takes the commission out on its
  // own now, and the flagged book is never an exchange, so the price printed is
  // the one a punter walks in and takes.
  const board = match;
  const found = [];
  // ONE ANSWER PER MATCH, not per side: how much has been matched and how long
  // there is to go are facts about the market, and asking per runner would be
  // the same question twelve times.
  const trust = exchangeTrustFor(match, { now });

  for (const side of pricedSides(board)) {
    const rows = (board?.prices?.[side] ?? []).filter((r) => usable(r) && !isExchange(r.bookmaker));
    const best = longest(rows);
    if (!best) continue;

    // The whole market first — that is the count the threshold is about.
    const market = consensus(board, side, { devig, commission });
    if (!market || market.books < booksFloor) continue;

    const fair = consensus(board, side, { devig, exclude: best.bookmaker, commission });
    if (!fair || !(fair.pFair > 0)) continue;

    // WHAT THE LONGEST PRICE IS MEASURED AGAINST — see the block above. The
    // exchange where there is one, and it needs no correction because nothing
    // is being maximised against it; the rest of the board otherwise, with the
    // expected-maximum artefact taken off.
    //
    // THE CORRECTION COMES OFF THE REFERENCE, NOT OFF THE ANSWER, and that is
    // deliberate. `edge = price x pFair - 1` either way — subtracting
    // `price x c(n)` from the edge and subtracting `c(n)` from the probability
    // are the same arithmetic — but only one of them leaves a screen able to
    // print two numbers a punter can check against each other. A cell that said
    // "fair $2.40, 4% over" where 2.15 / 2.40 is not 4% would be the app
    // showing its working and getting it wrong. So `pFair` is the price this
    // finding is actually judged against, `pMarket` is the market's own
    // untouched opinion, and the gap between them is `correction`.
    // THE GATE. On a race the exchange is the reference only once the money has
    // arrived — enough of it for the code, and close enough to the jump (or, on
    // a key that cannot see volume, close enough on the clock alone). Untrusted
    // and the reference is the rest of the board, exactly as it is on a race the
    // exchange is not on at all. A fixture never fails the gate.
    const exchange = trust.trusted ? exchangeReference(board, side, devig, commission) : null;
    const quotes = rows.length;
    const sigma = exchange
      ? 0
      : dispersion(rows.filter((r) => r !== best).map((r) => 1 / r.price));
    const correction = exchange ? 0 : bestOfNCorrection(quotes, sigma);
    const pMarket = exchange ? exchange.pFair : fair.pFair;
    const pFair = pMarket - correction;
    if (!(pFair > 0)) continue;

    const edge = best.price * pFair - 1;
    if (!(edge >= edgeFloor)) continue;

    found.push({
      matchKey: match.matchKey,
      sport: match.sport,
      competition: match.competition ?? '',
      kickoff: match.kickoff,
      side,
      name: sideName(board, side),
      bookmaker: best.bookmaker,
      bookmakerName: best.bookmakerName ?? best.bookmaker,
      price: best.price,
      pFair,
      fair: 1 / pFair,
      edge,
      // WHOSE FAIR PRICE THIS IS: 'exchange' when the money priced the side and
      // the comparison is one price against one price, 'board' when it is the
      // rest of the shops averaged and the best-of-n artefact has been taken
      // off. `pMarket` is that untouched average, `correction` is what came off
      // it (in probability, not in price), and `quotes` is the n it was keyed
      // on — so a screen or a test can show the working rather than trust it.
      reference: exchange ? 'exchange' : 'board',
      // WHY that reference, where the gate had a say: `{ trusted, reason,
      // basis, floor, totalMatched, minutesToJump }`. A screen prints the
      // sentence off this rather than deriving it a second time, and a
      // fixture's is always trusted.
      trust,
      pMarket,
      marketFair: 1 / pMarket,
      correction,
      quotes,
      dispersion: sigma,
      // The books the price is out of line WITH — the rest of the market — and,
      // separately, the whole count the threshold was read against. Both are
      // counts of shops whichever yardstick `reference` names: the exchange
      // replaces the fair price, not the market the flag sits in.
      books: fair.books,
      marketBooks: market.books,
    });
  }

  return found.sort(byEdge);
}

/**
 * One match where the best price on every side, taken together, adds to less
 * than a whole — back all of them and the return is the same whichever way it
 * goes.
 *
 * ON A RACE THAT IS A DUTCH ACROSS THE ACTIVE FIELD, and it is the same
 * arithmetic and the same words on the screen: every runner still in the race
 * backed at its best price, staked to return the same whichever one of them
 * wins, and the app calls it "Covered" exactly as it does a two-way market.
 *
 * The exchange IS allowed to carry a leg here, deflated, because covering a
 * book takes the offer that is there rather than a price to be shopped later —
 * and the leg says `exchange` so a screen can warn that the price moves.
 *
 * Two guards, both of them against a price that is not a price. Every leg's
 * book must quote a sane full vector on this match (`bookFair` answers null
 * otherwise), which is what keeps a stale one-sided 1000 out; and the implied
 * total must still sit inside the overround band's floor, because a "20%
 * guaranteed" is a broken feed and never a market.
 */
export function arbsFor(match, { devig = DEVIG, commission = EXCHANGE_COMMISSION } = {}) {
  // THE ONE PLACE A DEFLATED BOARD IS STILL WANTED: a dutch splits real money
  // across real prices, and the exchange's leg has to be staked at what it
  // pays. The completeness check below goes to the UNDEFLATED match, because
  // `bookFair` now takes the commission out itself.
  const board = deflated(match, commission);
  const sides = pricedSides(board);
  if (sides.length < 2) return null;

  const legs = [];
  for (const side of sides) {
    const rows = board.prices[side]
      .filter((r) => usable(r) && bookFair(match, side, r.bookmaker, devig, { commission }) !== null);
    const best = longest(rows);
    if (!best) return null;
    legs.push({ side, row: best });
  }

  const total = legs.reduce((sum, l) => sum + 1 / l.row.price, 0);
  if (!(total < 1) || total < overroundBandFor(board)[0]) return null;

  return {
    matchKey: match.matchKey,
    sport: match.sport,
    competition: match.competition ?? '',
    kickoff: match.kickoff,
    margin: 1 - total,
    legs: legs.map(({ side, row }) => ({
      side,
      name: sideName(board, side),
      bookmaker: row.bookmaker,
      bookmakerName: row.bookmakerName ?? row.bookmaker,
      price: row.price,
      stakeShare: (1 / row.price) / total,
      exchange: isExchange(row.bookmaker),
    })),
  };
}

/** The matches in a universe, whether it is the store's Map or a bare list. */
function matchesOf(universe) {
  const source = universe?.matches ?? universe;
  if (!source) return [];
  if (typeof source.values === 'function') return [...source.values()];
  return Array.isArray(source) ? [...source] : [];
}

/**
 * The fixtures still to start. `now` left out means no cut at all — the same
 * rule `board-view.js` reads its universe by, so the two agree about which
 * matches are on the board.
 */
function stillToStart(universe, now) {
  const at = Number(now);
  const all = matchesOf(universe);
  if (!Number.isFinite(at)) return all;
  return all.filter((match) => {
    const start = new Date(match?.kickoff).getTime();
    return !Number.isFinite(start) || start > at;
  });
}

/** Every outlier on the board, longest edge first. */
export function findOutliers(universe, { now = null, ...opts } = {}) {
  // The same clock does both jobs: which matches are still to start, and how
  // long a race has to go — which is half of whether its exchange price is
  // worth believing. A caller that passes no clock gets no time-based trust,
  // which on a delayed key means the local books, which is the safe answer.
  return stillToStart(universe, now)
    .flatMap((match) => outliersFor(match, { ...opts, now }))
    .sort(byEdge);
}

/** Every covered match on the board, biggest margin first. */
export function findArbs(universe, { now = null, ...opts } = {}) {
  return stillToStart(universe, now)
    .map((match) => arbsFor(match, opts))
    .filter(Boolean)
    .sort((a, b) => b.margin - a.margin);
}
